import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CheckCircle, Mail } from "lucide-react";

const SHOW_CRM_WIDGET = true; // Toggle: set false to use built-in form

const EmailCapture = () => {
  const [ipAddress, setIpAddress] = useState("");
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");
  const widgetRef = useRef(null);
  const scriptInjected = useRef(false);

  // Capture visitor IP silently
  useEffect(() => {
    fetch("https://api.ipify.org?format=json")
      .then((res) => res.json())
      .then((data) => setIpAddress(data.ip))
      .catch(() => setIpAddress("unavailable"));
  }, []);

  // Inject CRM widget — only once, guarded against StrictMode double-fire
  useEffect(() => {
    if (!SHOW_CRM_WIDGET) return;
    if (scriptInjected.current) return;
    if (document.querySelector('script[data-token]')) return;

    scriptInjected.current = true;

    const u = "https://revenutize.com";
    const t = "5af929d26b19622b9222f53b80009e994e5c75c8358c0d35745f334f6ddbbb6c";

    const s = document.createElement("script");
    s.src = u + "/api/public/forms/widget.js";
    s.setAttribute("data-token", t);
    s.setAttribute("data-base-url", u);
    s.async = true;

    // Append to body — widget targets the data-cf-token div directly
    document.body.appendChild(s);
  }, []);

  // Once IP is fetched, inject it into Revenutize's hidden ip_address field
  useEffect(() => {
    if (!ipAddress || !SHOW_CRM_WIDGET) return;

    // Poll briefly for the widget to render its hidden field, then fill it
    const fill = () => {
      const target = document.querySelector(
        '[data-cf-token] input[name="ip_address"], [data-cf-token] input[type="hidden"]'
      );
      if (target) {
        target.value = ipAddress;
        console.log("IP injected into Revenutize field:", ipAddress);
      }
    };

    // Try immediately and after short delays to catch widget render timing
    fill();
    const t1 = setTimeout(fill, 500);
    const t2 = setTimeout(fill, 1500);
    const t3 = setTimeout(fill, 3000);

    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
  }, [ipAddress]);

  const handleSubmit = (e) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setError("Please enter a valid email address.");
      return;
    }
    setError("");
    console.log("Waitlist signup:", { email: trimmed, ip: ipAddress, timestamp: new Date().toISOString() });
    setSubmitted(true);
  };

  return (
    <section className="py-24 md:py-32 px-6">
      {/* Hidden IP field */}
      <input type="hidden" name="ip_address" value={ipAddress} readOnly />

      <div className="max-w-2xl mx-auto text-center">
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="font-manrope text-xs sm:text-sm tracking-[0.25em] uppercase text-[#A25AFF] font-bold mb-4"
        >
          Be First in Line
        </motion.p>

        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="font-oswald text-4xl sm:text-5xl font-bold tracking-tight text-[#F0F0EE] mb-4"
        >
          Know before everyone else.
        </motion.h2>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.15 }}
          className="font-manrope text-[#8A8A93] text-base sm:text-lg mb-10"
        >
          Get notified when the Dallas beta launches. No spam — just the drop.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          {SHOW_CRM_WIDGET ? (
            /* CRM Widget — data-cf-token div is the render target for widget.js */
            <div
              data-cf-token="5af929d26b19622b9222f53b80009e994e5c75c8358c0d35745f334f6ddbbb6c"
              data-testid="crm-widget-container"
              ref={widgetRef}
              className="w-full min-h-[60px]"
            />
          ) : (
            /* Built-in fallback form */
            <AnimatePresence mode="wait">
              {!submitted ? (
                <motion.form
                  key="form"
                  onSubmit={handleSubmit}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="flex flex-col sm:flex-row gap-3"
                >
                  <div className="relative flex-1">
                    <Mail size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#8A8A93]" />
                    <input
                      data-testid="email-capture-input"
                      type="email"
                      value={email}
                      onChange={(e) => { setEmail(e.target.value); setError(""); }}
                      placeholder="your@email.com"
                      className="w-full bg-white/5 border border-white/10 rounded-md focus:border-[#FF641E] focus:outline-none focus:ring-1 focus:ring-[#FF641E] pl-10 pr-4 py-4 text-[#F0F0EE] placeholder-[#8A8A93]/60 font-manrope text-base transition-colors"
                    />
                  </div>
                  <button
                    data-testid="email-capture-submit"
                    type="submit"
                    className="bg-[#FF641E] text-[#0D0D11] font-manrope font-bold px-8 py-4 rounded-md hover:opacity-90 hover:scale-105 transition-all duration-300 whitespace-nowrap text-base"
                  >
                    Notify Me
                  </button>
                </motion.form>
              ) : (
                <motion.div
                  key="success"
                  data-testid="email-capture-success"
                  initial={{ opacity: 0, y: 10, scale: 0.98 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  transition={{ duration: 0.4 }}
                  className="flex flex-col sm:flex-row items-center justify-center gap-3 bg-white/[0.03] border border-[#FF641E]/30 rounded-md px-6 py-5"
                >
                  <CheckCircle size={22} className="text-[#FF641E] flex-shrink-0" />
                  <p className="font-manrope text-[#F0F0EE] text-base">
                    You're on the list. We'll be in touch before Dallas beta launches.
                  </p>
                </motion.div>
              )}
            </AnimatePresence>
          )}

          {error && (
            <p data-testid="email-capture-error" className="font-manrope text-red-400 text-sm mt-3">{error}</p>
          )}
        </motion.div>
      </div>
    </section>
  );
};

export default EmailCapture;
